import React, { useMemo } from "react";
import GlobalStyles from "@mui/joy/GlobalStyles";
import IconButton from "@mui/joy/IconButton";
import Sheet from "@mui/joy/Sheet";
import { ColorSchemeToggle } from "../ColorSchemeToggle/index";
import { Home, Logout, Wifi, WifiOff } from "@mui/icons-material";

import { CONNECTION_STATE_CHANGE, ConnectionState } from "aws-amplify/api";
import { Hub } from "aws-amplify/utils";
import { Box, Chip, Tooltip, Typography } from "@mui/joy";
import { useLocation, useNavigate } from "react-router-dom";
import { signOut } from "aws-amplify/auth";
import { useAuthenticator } from "@aws-amplify/ui-react";

export const Header = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const { authStatus } = useAuthenticator((context) => [context.authStatus]);

    const authenticated = useMemo(() => authStatus === "authenticated", [authStatus]);

    const [connectionState, setConnectionState] = React.useState<any>();

    Hub.listen("api", (data: any) => {
        const { payload } = data;
        if (payload.event === CONNECTION_STATE_CHANGE) {
            const connectionState = payload.data.connectionState as ConnectionState;
            setConnectionState(connectionState);
        }
    });

    const isConnected = connectionState === "Connected";
    const isDisconnected =
        connectionState === "Disconnected" ||
        connectionState === "ConnectionDisrupted" ||
        connectionState === "ConnectionDisruptedPendingNetwork" ||
        connectionState === "ConnectedPendingNetwork" ||
        connectionState === "ConnectedPendingKeepAlive" ||
        connectionState === "ConnectedPendingDisconnect";

    return (
        <Sheet
            sx={(theme) => ({
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                width: "100%",
                height: "var(--Header-height)",
                zIndex: 9998,
                px: { xs: 2, sm: 3 },
                py: 1.5,
                gap: 2,
                borderBottom: "1px solid",
                borderColor: "divider",
                backdropFilter: "blur(12px)",
                backgroundColor: "rgba(var(--joy-palette-background-surfaceChannel) / 0.8)",
                [theme.getColorSchemeSelector("dark")]: {
                    backgroundColor: "rgba(var(--joy-palette-background-surfaceChannel) / 0.9)",
                },
            })}
        >
            <GlobalStyles
                styles={(theme) => ({
                    ":root": {
                        "--Header-height": "60px",
                    },
                    "@supports (-webkit-touch-callout: none)": {
                        ".content-area": {
                            paddingBottom: "env(safe-area-inset-bottom)",
                        },
                    },
                })}
            />

            {/* Left: Home button */}
            <IconButton
                onClick={() => navigate(authenticated ? "/dashboard" : "/")}
                variant="soft"
                color="neutral"
                size="sm"
                sx={{
                    borderRadius: "xl",
                    transition: "all 0.2s ease",
                    "&:hover": {
                        transform: "scale(1.05)",
                    },
                }}
            >
                <Home />
            </IconButton>

            {/* Center: Connection status (only on room pages) */}
            {location.pathname.includes("/room") && (
                <Box
                    sx={{
                        flex: 1,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    {isConnected && (
                        <Tooltip
                            title="Real-time updates enabled — no need to refresh"
                            variant="soft"
                            placement="bottom"
                        >
                            <Chip
                                variant="soft"
                                color="success"
                                size="sm"
                                startDecorator={<Wifi sx={{ fontSize: 16 }} />}
                                sx={{
                                    px: 1.5,
                                    fontWeight: 500,
                                    cursor: "default",
                                }}
                            >
                                <Typography
                                    level="body-xs"
                                    sx={{
                                        display: { xs: "none", sm: "block" },
                                    }}
                                >
                                    Live
                                </Typography>
                            </Chip>
                        </Tooltip>
                    )}
                    {isDisconnected && (
                        <Tooltip
                            title="Connection lost — please refresh to get latest updates"
                            variant="soft"
                            color="danger"
                            placement="bottom"
                        >
                            <Chip
                                variant="soft"
                                color="danger"
                                size="sm"
                                startDecorator={<WifiOff sx={{ fontSize: 16 }} />}
                                sx={{
                                    px: 1.5,
                                    fontWeight: 500,
                                    cursor: "pointer",
                                }}
                                onClick={() => window.location.reload()}
                            >
                                <Typography
                                    level="body-xs"
                                    sx={{
                                        display: { xs: "none", sm: "block" },
                                    }}
                                >
                                    Disconnected
                                </Typography>
                            </Chip>
                        </Tooltip>
                    )}
                </Box>
            )}

            {/* Right: Actions */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <ColorSchemeToggle
                    sx={{
                        borderRadius: "xl",
                        transition: "all 0.2s ease",
                        "&:hover": {
                            transform: "scale(1.05)",
                        },
                    }}
                />
                {authenticated && (
                    <Tooltip title="Sign out" variant="soft" placement="bottom">
                        <IconButton
                            onClick={async () => {
                                await signOut();
                                navigate("/");
                            }}
                            variant="soft"
                            color="neutral"
                            size="sm"
                            sx={{
                                borderRadius: "xl",
                                transition: "all 0.2s ease",
                                "&:hover": {
                                    transform: "scale(1.05)",
                                    color: "danger.500",
                                },
                            }}
                        >
                            <Logout />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
        </Sheet>
    );
};
